import { useState } from "react";
import Styles from "./StatsTable.module.scss";
import { TabViewChooser } from "src/components/compounds/TabViewChooser/TabViewChooser";
import { PillDropdown } from "src/components/atoms/PillDropdown/PillDropdown";
import { Spacer } from "src/components/atoms/Spacer/Spacer";
import { OldRow } from "src/components/atoms/OldGrid/OldRow";
import { defaultGridOptions, buildTeamTotalGridItem, EventDateColumn } from "../../pages/Teams/stats-columns/common-columns";
import { StatsInfo } from "../GameStats/stat-info-type";
import * as Eden from "src/eden/content";
import {
  catchersGridDataFormatted,
  FieldingCatching,
} from "../../pages/Teams/stats-columns/baseball/fielding-catching";
import {
  inningsPlayedGridDataFormatted,
  FieldingInningsPlayed,
} from "../../pages/Teams/stats-columns/baseball/fielding-innings-played";
import {
  fieldingGridDataFormatted,
  FieldingStandard,
} from "../../pages/Teams/stats-columns/baseball/fielding-standard";
import {
  BattingStandard,
  battingStandardGridDataFormatted,
} from "../../pages/Teams/stats-columns/baseball/batting-standard";
import {
  BattingAdvanced,
  battingAdvancedGridDataFormatted,
} from "../../pages/Teams/stats-columns/baseball/batting-advanced";
import {
  PitchingStandard,
  pitchingStandardGridDataFormatted,
} from "../../pages/Teams/stats-columns/baseball/pitching-standard";
import {
  PitchingAdvanced,
  pitchingAdvancedGridDataFormatted,
} from "../../pages/Teams/stats-columns/baseball/pitching-advanced";
import {
  PitchingBreakdown,
  pitchingBreakdownGridDataFormatted,
} from "../../pages/Teams/stats-columns/baseball/pitching-breakdown";
import StatsDataGrid from "../StatsDataGrid/StatsDataGrid";
import StatsLegend from "../StatsLegend/StatsLegend";

type StatsTableProps = {
  grid: any; // Replace `any` with the appropriate type
  // Season team-total blob; when present a bold "Team" row is pinned to the bottom of every view.
  totals?: Eden.StatData;
  // Per-game (player-profile) mode: each row is one game, so prepend a chronological Date column
  // and relabel the leading column "Opponent" (it already shows the opponent, linked to the box score).
  eventMode?: boolean;
};

const standardView = ["Standard", "Advanced"];
const pitchingView = ["Standard", "Advanced", "Breakdown"];
const fieldingView = ["Standard", "Catching", "Innings Played"];
const mainViewOptions = ["Batting", "Pitching", "Fielding"];

// Turns a season column set into a per-game one: a pinned Date column (sorted oldest → newest)
// leads, the leading "Player" column is relabeled "Opponent", and every other column's default
// sort is cleared so Date stays the sole sort.
const toEventColumns = (columns: StatsInfo[]): StatsInfo[] => [
  EventDateColumn,
  ...columns.map((col, i) => {
    const cleared: StatsInfo = { ...col, sort: undefined };
    return i === 0 ? { ...cleared, name: "Opponent" } : cleared;
  }),
];

export default function StatsTable({ grid, totals, eventMode = false }: StatsTableProps) {
  const [currentMainView, setCurrentMainView] = useState(mainViewOptions[0]);
  const [currentView, setCurrentView] = useState(standardView[0]);

  const handleActive = (view: string) => {
    setCurrentView(view);
  };

  const handleMainActive = (view: string) => {
    setCurrentMainView(view);
    setCurrentView(standardView[0]);
  };

  // The team-total row is run through the SAME per-table formatter as the player rows, so it
  // formats identically (right columns, rate precision) for every tab and sub-view.
  const totalsGrid = totals ? [buildTeamTotalGridItem(totals)] : [];

  const gridDataMapping: Record<string, () => any> = {
    Batting: () => ({
      standard: battingStandardGridDataFormatted(grid),
      advanced: battingAdvancedGridDataFormatted(grid),
      standardTotals: battingStandardGridDataFormatted(totalsGrid),
      advancedTotals: battingAdvancedGridDataFormatted(totalsGrid),
      columnDefs: {
        standard: BattingStandard,
        advanced: BattingAdvanced,
      },
    }),
    Pitching: () => ({
      standard: pitchingStandardGridDataFormatted(grid),
      advanced: pitchingAdvancedGridDataFormatted(grid),
      breakdown: pitchingBreakdownGridDataFormatted(grid),
      standardTotals: pitchingStandardGridDataFormatted(totalsGrid),
      advancedTotals: pitchingAdvancedGridDataFormatted(totalsGrid),
      breakdownTotals: pitchingBreakdownGridDataFormatted(totalsGrid),
      columnDefs: {
        standard: PitchingStandard,
        advanced: PitchingAdvanced,
        breakdown: PitchingBreakdown,
      },
    }),
    Fielding: () => ({
      standard: fieldingGridDataFormatted(grid),
      catching: catchersGridDataFormatted(grid),
      inningsPlayed: inningsPlayedGridDataFormatted(grid),
      standardTotals: fieldingGridDataFormatted(totalsGrid),
      catchingTotals: catchersGridDataFormatted(totalsGrid),
      inningsPlayedTotals: inningsPlayedGridDataFormatted(totalsGrid),
      columnDefs: {
        standard: FieldingStandard,
        catching: FieldingCatching,
        inningsPlayed: FieldingInningsPlayed,
      },
    }),
  };

  const {
    standard,
    advanced,
    breakdown,
    catching,
    inningsPlayed,
    columnDefs,
    standardTotals,
    advancedTotals,
    breakdownTotals,
    catchingTotals,
    inningsPlayedTotals,
  } = gridDataMapping[currentMainView]() || {};

  const selectedGrid =
    currentView === fieldingView[1]
      ? catching
      : currentView === fieldingView[2]
      ? inningsPlayed
      : currentView === "Breakdown"
      ? breakdown
      : currentView === standardView[1]
      ? advanced
      : standard;

  const selectedTotals =
    currentView === fieldingView[1]
      ? catchingTotals
      : currentView === fieldingView[2]
      ? inningsPlayedTotals
      : currentView === "Breakdown"
      ? breakdownTotals
      : currentView === standardView[1]
      ? advancedTotals
      : standardTotals;

  const baseColumnDefs =
    currentView === fieldingView[1]
      ? columnDefs.catching
      : currentView === fieldingView[2]
      ? columnDefs.inningsPlayed
      : currentView === "Breakdown"
      ? columnDefs.breakdown
      : currentView === standardView[1]
      ? columnDefs.advanced
      : columnDefs.standard;

  const selectedColumnDefs = eventMode ? toEventColumns(baseColumnDefs) : baseColumnDefs;

  return (
    <>
      <div className={Styles["tab-view-grid"]}>
        <div className={Styles["tab-view"]}>
          <OldRow>
            <TabViewChooser options={mainViewOptions} active={currentMainView} onClick={handleMainActive} />
          </OldRow>
          <OldRow className={Styles.selectorRow}>
            <PillDropdown
              options={
                currentMainView === mainViewOptions[2]
                  ? fieldingView
                  : currentMainView === mainViewOptions[1]
                  ? pitchingView
                  : standardView
              }
              value={currentView}
              onChange={handleActive}
              data-testid="stats-selector"
            />
          </OldRow>
        </div>
      </div>
      <Spacer.Vertical />
      <StatsDataGrid
        grid={selectedGrid}
        pinnedBottomRow={selectedTotals}
        gridOptions={defaultGridOptions}
        columnDefs={selectedColumnDefs}
      />
      <StatsLegend
        columns={selectedColumnDefs}
        showPositionKey={currentMainView === mainViewOptions[2] && currentView === fieldingView[2]}
      />
    </>
  );
}
